// file: src/components/ai/AiPanel.jsx
import React, { useMemo, useRef, useState } from "react";
import AiService from "./AiService";
import { insertAtCursor } from "./insertAtCursor";
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { getOpenAIKey, getDeepSeekKey, getOpenRouterKey } from '../../utils/Env';

// You can replace/extend this with a backend-powered list later.
const AVAILABLE_MODELS = [
    { id: "gpt-4o", label: "GPT‑4o" },
    { id: "gpt-4o-mini", label: "GPT‑4o Mini" },
    { id: "claude-3-5-sonnet", label: "Claude 3.5 Sonnet" },
    { id: "gemini-1.5-pro", label: "Gemini 1.5 Pro" },
    { id: "deepseek-chat", label: "DeepSeek Chat" },
    { id: "llama-3-8b", label: "Llama-3 (Free)" },
];

// Map models to API key providers (avoids runtime process.env usage)
const getApiKeyForModel = (m) => {
    if (m === 'gpt-3.5-turbo' || m === 'gpt-4o' || m === 'gpt-4o-mini') return getOpenAIKey();
    if (m === 'deepseek-chat') return getDeepSeekKey();
    if (m === 'llama-3-8b' || m === 'claude-3-5-sonnet' || m === 'gemini-1.5-pro') return getOpenRouterKey();
    return undefined;
};

export default function AiPanel({ isOpen, onClose }) {
    const [model, setModel] = useState(AVAILABLE_MODELS[0].id);
    const [messages, setMessages] = useState([
        { role: "system", content: "You are an assistant for the LMS." },
    ]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [panelWidth, setPanelWidth] = useState(380);
    const listRef = useRef(null);
    const toastRef = useRef(null);
    // Resizer logic
    const resizerRef = useRef();
    const panelRef = useRef();
    React.useEffect(() => {
        const resizer = resizerRef.current;
        function onMouseDown(e) {
            const panel = panelRef.current;
            if (!panel) return;
            const startX = e.clientX;
            const startWidth = panel.offsetWidth;
            function onMouseMove(ev) {
                const newWidth = Math.max(280, Math.min(900, startWidth + (startX - ev.clientX)));
                setPanelWidth(newWidth);
            }
            function onMouseUp() {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
            }
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        }
        function onDoubleClick() { setPanelWidth(380); }
        if (resizer) {
            resizer.addEventListener('mousedown', onMouseDown);
            resizer.addEventListener('dblclick', onDoubleClick);
        }
        return () => {
            if (resizer) {
                resizer.removeEventListener('mousedown', onMouseDown);
                resizer.removeEventListener('dblclick', onDoubleClick);
            }
        };
    }, []);

    React.useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages]);

    const visibleMessages = useMemo(
        () => messages.filter((m) => m.role !== "system"),
        [messages]
    );

    async function onSend() {
        const content = input.trim();
        if (!content || sending) return;

        const newMessages = [...messages, { role: "user", content }];
        setMessages(newMessages);
        setInput("");
        setSending(true);

        try {
            const apiKey = getApiKeyForModel(model);
            const res = await AiService.chat({ model, messages: newMessages, apiKey });
            const assistantContent = res?.content ?? JSON.stringify(res);
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: String(assistantContent) },
            ]);
            setTimeout(() => {
                if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
            }, 0);
        } catch (e) {
            const errMsg = e?.message || 'Error while getting response.';
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: errMsg },
            ]);
            if (toastRef.current) toastRef.current.show({ severity: 'error', summary: 'AI Error', detail: errMsg, life: 4000 });
        } finally {
            setSending(false);
        }
    }

    function onKeyDown(e) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    }

    function copyText(text) {
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(text);
        }
    }

    function insertText(text) {
        insertAtCursor(text);
    }

    if (!isOpen) return null;

    return (
        <div
            ref={panelRef}
            style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: panelWidth,
                height: '100vh',
                background: '#fff',
                color: '#222',
                zIndex: 9999,
                boxShadow: '0 0 12px rgba(0,0,0,0.12)',
                borderLeft: '1px solid #eee',
                display: 'flex',
                flexDirection: 'column',
                padding: 0
            }}
        >
            <Toast ref={toastRef} position="top-right" />
            <div
                ref={resizerRef}
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 6,
                    height: '100%',
                    cursor: 'ew-resize',
                    zIndex: 10000,
                    background: 'rgba(0,0,0,0.01)'
                }}
            />
            <div style={{height: '100vh', display: 'flex', flexDirection: 'column'}}>
                <div className="p-panel-header" style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee', padding: 10 }}>
                    <span style={{ fontWeight: 600 }}>AI Assistant</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Button icon="pi pi-times" onClick={onClose} className="p-button-rounded p-button-text" title="Close" />
                    </div>
                </div>
                <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {visibleMessages.map((m, idx) => (
                        <div
                            key={idx}
                            className={m.role === 'user' ? 'p-message p-message-info' : 'p-message p-message-success'}
                            style={{ position: 'relative', borderRadius: 8, padding: '8px 10px', maxWidth: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word', alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start' }}
                        >
                            {m.role === "assistant" && (
                                <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 6 }}>
                                    <Button label="Copy" icon="pi pi-copy" className="p-button-sm p-button-text" onClick={() => copyText(m.content)} />
                                    <Button label="Insert" icon="pi pi-arrow-down" className="p-button-sm p-button-text" onClick={() => insertText(m.content)} />
                                </div>
                            )}
                            <pre style={{ margin: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>{m.content}</pre>
                        </div>
                    ))}
                </div>
                <div style={{ borderTop: '1px solid #eee', padding: 8, display: 'flex', gap: 8, alignItems: 'center', background: '#fafafa' }}>
                    <Dropdown
                        value={model}
                        options={AVAILABLE_MODELS}
                        onChange={e => setModel(e.value)}
                        optionLabel="label"
                        optionValue="id"
                        style={{ minWidth: 180, alignSelf: 'center' }}
                        aria-label="Model selector"
                    />
                    <InputTextarea
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Ask something..."
                        autoResize
                        style={{ flex: 1, minHeight: 38, maxHeight: 140, borderRadius: 6, background: '#fff', color: '#222', border: '1px solid #eee', fontSize: 15, resize: 'vertical' }}
                        aria-label="Message input"
                    />
                    <Button
                        icon={sending ? "pi pi-spin pi-spinner" : "pi pi-send"}
                        onClick={onSend}
                        disabled={sending || !input.trim()}
                        className="p-button-rounded"
                        style={{ marginLeft: 4, background: '#f5f5f5', color: '#222', border: '1px solid #eee', borderRadius: 20, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        aria-label="Send"
                        title="Send"
                    />
                </div>
            </div>
        </div>
    );
}
